import {
  Arrange,
  Container,
  FormField,
  Radio,
  Spacer,
  Text,
} from '@loomhq/lens';
import _shuffle from 'lodash/shuffle';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateWelcomeRole } from 'actions/welcome';
import {
  ONBOARDING_JOB_ROLES_WITH_MESSAGE,
  OTHER,
} from 'constants/employmentRoles';
import { COMPLETED_ONBOARDING_STEP } from 'constants/events';
import { ONBOARDING_STEP_COMPLETE } from 'constants/metrics';
import { PRIMARY_WEB_APP } from 'constants/onboarding';
import { patchUserPersonaInfoCreator } from 'creators/welcome';
import * as analytics from 'utilities/analytics';
import { incrementMetric } from 'utilities/metrics';

import { WORK_JOB_DESCRIPTION_STEP } from '../../constants';
import { DefaultCta, MobileScrollContainer } from '../../cta';
import { PageHeader, PageSubHeader } from '../../headers';

const JOB_OTHER = ONBOARDING_JOB_ROLES_WITH_MESSAGE.find(
  job => job.role == OTHER
);

const JOB_OPTIONS = [
  ..._shuffle(
    ONBOARDING_JOB_ROLES_WITH_MESSAGE.filter(job => job.role !== OTHER)
  ),
  JOB_OTHER,
];

function JobDescriptionPicker(props) {
  const { selection, setSelection } = props;

  return (
    <Container width="100%">
      <Arrange autoFlow="row" gap={2}>
        {JOB_OPTIONS.map(job => {
          const isSelected = selection === job.role;

          return (
            <div key={job.role}>
              <FormField
                label={job.role}
                direction="row"
                labelFor={job.role}
                key={job.role}
              >
                <Radio
                  id={job.role}
                  name={job.role}
                  isChecked={isSelected}
                  onChange={() => {
                    setSelection(job.role);
                  }}
                />
              </FormField>
              {isSelected && (
                <Spacer left={3.5}>
                  <Text color="grey6">{job.message}</Text>
                </Spacer>
              )}
            </div>
          );
        })}
      </Arrange>
    </Container>
  );
}

async function submitHandler(
  dispatch,
  selection,
  currentCustomRole,
  isLastPersonaStep,
  nextClickHandler = () => {}
) {
  if (!selection) {
    return;
  }

  const updateObj = {
    persona_role: selection,
    ...(currentCustomRole && {
      custom_role: currentCustomRole,
    }),
  };

  await dispatch(
    patchUserPersonaInfoCreator({
      ...updateObj,
      complete: isLastPersonaStep,
    })
  );

  analytics.track(COMPLETED_ONBOARDING_STEP, {
    step: WORK_JOB_DESCRIPTION_STEP,
    is_simiplified_onboarding: true,
    source: PRIMARY_WEB_APP,
    ...updateObj,
  });

  incrementMetric(ONBOARDING_STEP_COMPLETE, {
    step: WORK_JOB_DESCRIPTION_STEP,
    source: PRIMARY_WEB_APP,
  });

  nextClickHandler();
}

function WorkJobDescriptionStep({
  nextClickHandler,
  isLastPersonaStep,
  isMobile,
}) {
  const dispatch = useDispatch();

  const currentCompanyRole = useSelector(state => state.welcome.role);
  const currentCustomRole = useSelector(state => state.welcome.custom_role);

  const nextHandler = () =>
    submitHandler(
      dispatch,
      currentCompanyRole,
      currentCustomRole,
      isLastPersonaStep,
      nextClickHandler
    );

  return (
    <>
      <MobileScrollContainer isMobile={isMobile}>
        <Spacer left={1} right={1}>
          <PageHeader text="What is your role at your company?" />
          <Spacer top="large" bottom={{ default: 'large', medium: 'xlarge' }}>
            <PageSubHeader text="We will set up your Workspace with examples based on your role." />
          </Spacer>
          <JobDescriptionPicker
            selection={currentCompanyRole}
            setSelection={value => dispatch(updateWelcomeRole(value))}
          />
        </Spacer>
      </MobileScrollContainer>

      <DefaultCta
        isMobile={isMobile}
        onClick={nextHandler}
        isDisabled={!currentCompanyRole}
      />
    </>
  );
}

export default WorkJobDescriptionStep;
